import React, {useEffect, useMemo, useState} from 'react'
import DatePicker from 'react-datepicker'
import {useTranslation} from 'react-i18next'
import {Table, TableBody, TableHead, TableRow} from '@material-ui/core'
import TableCell from '@material-ui/core/TableCell'
import {makeStyles} from '@material-ui/core/styles'

import {ProductType, ProductUsesType} from 'features/invoice/invoiceSlice'
import useInvoiceView from 'features/invoice/useInvoiceView'

import InvoiceViewStyle from 'pages/CustomerManagement/InvoiceView/Style'
import {
  CUSTOMER_HEAD,
  CUSTOMER_MOCK,
  CUSTOMER_POSITION_HEAD,
  CUSTOMER_POSITION_BODY,
  PRODUCER_HEAD,
  PRODUCER_POSITION_HEAD,
  INVOICE_DETAIL_POSITION_BODY,
  PROVIDER_BACKGROUND_POS,
  CUSTOMER_BACKGROUND_POS,
  DEFAULT_TEMPLATE_INVOICE,
} from 'pages/CustomerManagement/InvoiceView/constant'
import {dateToDashString, formatDateFromString} from 'helpers/dateHelper'

const useStyles = makeStyles(InvoiceViewStyle)

type CellSpan = {
  pos: number
  num: number
  style?: string
}
interface MergeCell {
  row: number
  rowSpan: CellSpan[]
  colSpan: CellSpan[]
}

interface TransactionStatementProps {
  totalAmount?: number
}

function TransactionStatement(props: TransactionStatementProps) {
  const {t} = useTranslation()
  const {totalAmount = 0} = props
  const classes = useStyles()

  const {
    baseFees,
    consumer,
    billingSummary,
    provider,
    usageDetail,
    product,
    templates,
    updateUsageSummaryAction,
    invoiceInformation,
    callSetInvoiceInformation,
  } = useInvoiceView()

  const [customerHead, setCustomerHead] = useState(CUSTOMER_HEAD)
  const [customerBody, setCustomerBody] = useState(CUSTOMER_MOCK)
  const [providerData, setProviderData] = useState(PRODUCER_HEAD)
  const [posProduct, setPosProduct] = useState(INVOICE_DETAIL_POSITION_BODY)

  const [billingDateInput, setBillingDateInput] = useState<Date>(new Date())
  const [billingPriceInput, setBillingPriceInput] = useState<string>('0')
  const [remarkInput, setRemarkInput] = useState<string>('')

  const getNum = (cellSpan: CellSpan[], col: number) => {
    const posData = cellSpan?.map((item) => item.pos)
    if (posData?.includes(col)) {
      return cellSpan?.find((item) => item.pos === col)?.num || 1
    }
    return 1
  }

  const getMergeCell = (data: MergeCell[], row: number, col: number) => {
    let rowSpan = 1
    let colSpan = 1
    if (!data.length) return {rowSpan, colSpan}
    const rowIds = data.map((item) => item.row)
    if (!rowIds.includes(row)) return {rowSpan, colSpan}

    // get rowSpan property
    const cellRowSpanData = data.find((item) => item.row === row)?.rowSpan
    if (cellRowSpanData?.length) {
      rowSpan = getNum(cellRowSpanData, col)
    }

    // get ColSpan property
    const cellColSpanData = data.find((item) => item.row === row)?.colSpan
    if (cellColSpanData?.length) {
      colSpan = getNum(cellColSpanData, col)
    }
    return {rowSpan, colSpan}
  }

  useEffect(() => {
    const newCustomerHead = [...customerHead]
    const newCustomerBody = [...customerBody]
    if (consumer) newCustomerHead[0][2] = consumer.name
    if (
      billingSummary?.billingMonth &&
      billingSummary?.billingPrice &&
      billingSummary?.issueDate
    ) {
      newCustomerHead[1][1] =
        formatDateFromString(billingSummary.billingMonth) ?? ''
      newCustomerBody[0][1] = billingSummary.billingPrice
      setRemarkInput(billingSummary.remarks ?? '')
      setBillingDateInput(new Date(billingSummary.issueDate))
    } else {
      setBillingDateInput(new Date())
      setRemarkInput('')
    }
    setBillingPriceInput(String(totalAmount))
    setCustomerHead(newCustomerHead)
    setCustomerBody(newCustomerBody)
  }, [consumer, billingSummary, totalAmount])

  useEffect(() => {
    if (billingSummary?.issueDate) return
    callSetInvoiceInformation({
      ...invoiceInformation,
      billing: {
        ...invoiceInformation?.billing,
        issueDate: dateToDashString(new Date()),
      },
    })
  }, [billingSummary])

  useEffect(() => {
    const newProducer = [...providerData]
    if (provider) {
      newProducer[0][2] = provider.companyName
      newProducer[0][4] = t('IGetFullName', {
        firstName: provider.ceoFirstName,
        lastName: provider.ceoLastName,
      })
      newProducer[1][1] = provider.contactNumber
      newProducer[2][1] = provider.address
      newProducer[3][1] = provider.contactNumber
      newProducer[4][1] = provider.contactName
    }
    setProviderData(newProducer)
  }, [provider])

  const productTypes = useMemo(
    () => Array.from(new Set(usageDetail?.map((item) => item.type))),
    [usageDetail],
  )

  const getSegment = (usages: ProductUsesType[], index: number) => {
    const segment: ProductUsesType | undefined = usages.find(
      ({minCount, maxCount}) =>
        index >= (minCount ?? 0) && (maxCount === null || index <= maxCount),
    )
    return segment
  }

  const getProductInfo = (productType: ProductType) => {
    const usageDetailItem = usageDetail
      ?.filter((item) => item.type === productType)
      ?.sort((a, b) => a.sequence - b.sequence)

    const usageCount = usageDetailItem?.length || 0
    const templateFinal = templates?.length
      ? templates
      : DEFAULT_TEMPLATE_INVOICE
    const usages =
      templateFinal.find((item) => item.productType === productType)?.usages ||
      []
    const discount = product?.pkgInfo.discount || 0
    let eegSummarySurcharge = 0
    let count = 1
    const dataRow = []
    while (count <= usageCount) {
      const row = getSegment(usages, count)
      // TODO: calc eegSummarySurcharge
      if (
        usageDetailItem &&
        usageDetailItem[count]?.patientAge &&
        (productType === 'eegSummary' ||
          productType === 'eegSummaryAdv' ||
          productType === 'eegSummarySurcharge') &&
        usageDetailItem[count].patientAge < 20
      ) {
        eegSummarySurcharge = product?.pkgInfo.eegSummarySurcharge || 0
      } else eegSummarySurcharge = 0

      if (!row) {
        count += usageCount - count + 1
        break
      }

      dataRow.push({
        productType,
        minCount: count,
        maxCount: row?.maxCount || '',
        count:
          row?.maxCount && row?.maxCount <= usageCount
            ? row?.maxCount - count + 1
            : usageCount - count + 1,
        price: Math.ceil(
          ((row?.price || 0) * (100 - discount + eegSummarySurcharge)) / 100,
        ),
      })
      if (!row?.maxCount) {
        count += usageCount - count + 1
        break
      }
      count += row?.maxCount
    }
    return dataRow
  }

  const renderTable = () => {
    const dataTable = productTypes
      .map((item) => {
        return getProductInfo(item as ProductType)
      })
      .flat()

    updateUsageSummaryAction(dataTable)
    let index = 0
    let priceBaseFees = 0
    let resultBaseFees: (string | number)[][] = []
    if (baseFees.length === 1) {
      resultBaseFees = [
        [
          index + 1,
          baseFees[0].name,
          1,
          baseFees[0].price,
          baseFees[0].price,
          '',
        ],
      ]
      index += 1
      priceBaseFees = baseFees[0].price
    } else if (baseFees.length === 2) {
      resultBaseFees = [
        [
          index + 1,
          baseFees[0].name,
          1,
          baseFees[0].price,
          baseFees[0].price,
          '',
        ],
        [
          index + 2,
          baseFees[1].name,
          1,
          baseFees[1].price,
          baseFees[1].price,
          '',
        ],
      ]
      index += 2
      priceBaseFees = baseFees[0].price + baseFees[1].price
    }
    const resultTemplate = dataTable.map((item) => {
      const rowData: (string | number | ProductType)[] = [
        index + 1,
        item.productType,
        item.count,
        item.price,
        item.price * item.count,
        '',
      ]
      index += 1
      if (index === 0) rowData.push(billingSummary?.issueDate ?? '')
      return rowData
    })

    const result = [...resultBaseFees, ...resultTemplate]
    const totalCount =
      dataTable.reduce((prev, next) => {
        return (prev ?? 0) + next.count
      }, 0) + baseFees.length

    const totalPrice =
      dataTable.reduce((prev, next) => {
        return (prev ?? 0) + next.price * next.count
      }, 0) + priceBaseFees

    result.push([t('ITotalAmountVAT'), totalCount, `₩${totalPrice}`, ''])
    result.push([t('ISignificant'), billingSummary?.remarks || ''])

    const newPosProduct = posProduct

    newPosProduct[0].row = result.length - 2
    newPosProduct[1].row = result.length - 1

    setPosProduct(newPosProduct)
  }

  const handleChangeRemark = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setRemarkInput(e.target.value)
  }
  const handleBlurRemark = () => {
    callSetInvoiceInformation({
      ...invoiceInformation,
      billing: {...invoiceInformation?.billing, remarks: remarkInput},
    })
  }

  useEffect(() => {
    if (productTypes && templates) renderTable()
  }, [templates, productTypes])

  return (
    <div className={classes.defaultBilling} style={{marginTop: 5}}>
      <div className={classes.defaultBillingTitle}>
        {t('ITransactionStatement')}
      </div>
      <div className={classes.defaultBillingTable}>
        <div className={classes.invoiceCommonInfo}>
          <Table>
            <TableHead>
              {customerHead.map((data, rowI) => (
                <TableRow key={`customer_${rowI}`}>
                  {data.map((cell, colI) => (
                    <TableCell
                      key={`cell_customer_${colI}`}
                      {...getMergeCell(CUSTOMER_POSITION_HEAD, rowI, colI)}
                      className={`${classes.headerMax} ${
                        CUSTOMER_BACKGROUND_POS.head.includes(`${rowI}${colI}`)
                          ? classes.backGroundHead
                          : ''
                      } ${
                        rowI === 0 && colI === 0 ? classes.verticalText : ''
                      }`}
                    >
                      {rowI === 1 && colI === 1 ? (
                        <DatePicker
                          selected={billingDateInput}
                          wrapperClassName={classes.inputBillingDateWrapper}
                          className={classes.inputBillingDate}
                          dateFormat='yyyy-MM-dd'
                          onChange={(date) =>
                            !!date && setBillingDateInput(date)
                          }
                          onBlur={() => {
                            callSetInvoiceInformation({
                              ...invoiceInformation,
                              billing: {
                                ...invoiceInformation?.billing,
                                issueDate: dateToDashString(billingDateInput),
                              },
                            })
                          }}
                        />
                      ) : (
                        cell
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableHead>
            <TableBody>
              {customerBody.map((data, rowI) => (
                <TableRow key={`customer_body_${rowI}`}>
                  {data.map((cell, colI) => (
                    <TableCell
                      key={`cell_customer_body_${colI}`}
                      {...getMergeCell(CUSTOMER_POSITION_BODY, rowI, colI)}
                      className={`${classes.header}  ${
                        CUSTOMER_BACKGROUND_POS.body.includes(`${rowI}${colI}`)
                          ? classes.backGroundHead
                          : ''
                      }`}
                    >
                      {rowI === customerBody.length - 1 && colI === 1
                        ? billingPriceInput
                        : cell}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <Table>
            <TableHead>
              {providerData.map((data, rowI) => (
                <TableRow key={`producer_${rowI}`}>
                  {data.map((cell, colI) => (
                    <TableCell
                      key={`cell_producer_body_${colI}`}
                      {...getMergeCell(PRODUCER_POSITION_HEAD, rowI, colI)}
                      className={`${rowI === 0 ? classes.header : classes.cell} 
                      ${
                        PROVIDER_BACKGROUND_POS.head.includes(`${rowI}${colI}`)
                          ? classes.backGroundHead
                          : ''
                      }
                      ${rowI === 0 && colI === 0 ? classes.verticalText : ''}`}
                    >
                      {cell}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableHead>
          </Table>
        </div>
        <div
          style={{
            marginTop: 10,
          }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell
                  className={`${classes.headerMax} ${classes.backGroundHead}`}
                >
                  {t('ISignificant')}
                </TableCell>
                <TableCell className={`${classes.headerMax}`}>
                  <textarea
                    value={remarkInput}
                    rows={5}
                    className={classes.inputDescription}
                    onChange={handleChangeRemark}
                    onBlur={handleBlurRemark}
                  />
                </TableCell>
              </TableRow>
            </TableHead>
          </Table>
        </div>
      </div>
    </div>
  )
}

export default TransactionStatement
